import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Loader2, Package, AlertTriangle, Building, User } from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseReceiptEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/mal-kabul');

  const [receipt, setReceipt] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    warehouse_id: '',
    invoice_number: '',
    invoice_date: '',
    department_id: '',
    receiver_employee_id: '',
    notes: '',
    items: []
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const receiptRes = await api.get(`/purchase-receipts/${id}`);
      const receiptData = receiptRes.data;
      setReceipt(receiptData);

      // Kritik olmayan endpoint'ler — hata verseler bile sayfayı çökertme
      const [warehousesRes, departmentsRes, employeesRes] = await Promise.allSettled([
        api.get('/warehouses'),
        api.get('/departments'),
        api.get('/employees')
      ]);
      setWarehouses(warehousesRes.status === 'fulfilled' ? (warehousesRes.value.data || []) : []);
      setDepartments(departmentsRes.status === 'fulfilled' ? (departmentsRes.value.data || []) : []);
      setEmployees(employeesRes.status === 'fulfilled' ? (employeesRes.value.data || []) : []);

      setForm({
        warehouse_id: receiptData.warehouse_id || '',
        invoice_number: receiptData.invoice_number || '',
        invoice_date: receiptData.invoice_date || '',
        department_id: receiptData.department_id || '',
        receiver_employee_id: receiptData.receiver_employee_id || '',
        notes: receiptData.notes || '',
        items: (receiptData.items || []).map(item => {
          // `received_qty` bekleyen kayitta BUGUNE KADARKI TOPLAM'dir.
          // Ekranda ayri tutulur; kutuya bu teslimatin varsayilani olarak
          // KALAN miktar yazilir.
          const siparis = parseFloat(item.ordered_qty || 0);
          const oncekiToplam = parseFloat(item.received_qty || 0);
          const kalan = Math.max(0, +(siparis - oncekiToplam).toFixed(6));
          return {
            ...item,
            onceki_toplam: oncekiToplam,
            kalan,
            received_qty: kalan ? String(kalan) : '',
            unit_price: item.unit_price?.toString() || '',
            is_return: item.is_return || false,
            return_reason: item.return_reason || ''
          };
        })
      });
    } catch (err) {
      toast.error('Mal kabul yüklenemedi');
      navigate('/erp/purchasing?tab=receipts');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index][field] = value;
    setForm(prev => ({ ...prev, items }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.warehouse_id) {
      toast.error('Lütfen depo seçin');
      return;
    }

    const hasReceivedItems = form.items.some(i => parseFloat(i.received_qty) > 0 || i.is_return);
    if (!hasReceivedItems) {
      toast.error('En az bir kalem için miktar girin veya iade işaretleyin');
      return;
    }

    setSaving(true);
    try {
      const res = await api.patch(`/purchase-receipts/${id}`, {
        ...form,
        items: form.items.map(i => ({
          product_card_id: i.product_card_id,
          product_name: i.product_name,
          ordered_qty: parseFloat(i.ordered_qty || 0),
          received_qty: parseFloat(i.received_qty || 0),
          unit: i.unit,
          unit_price: parseFloat(i.unit_price || 0),
          is_return: i.is_return,
          return_reason: i.return_reason,
          // Siparis hangi cevrim carpaniyla verildiyse onunla islensin
          base_unit: i.base_unit,
          base_factor: i.base_factor
        }))
      });
      const durum = res?.data?.bekleyen_durum;
      toast.success(durum === 'partial'
        ? 'Teslimat işlendi — kalan miktar açık kaldı'
        : 'Mal kabul tamamlandı ve stok güncellendi');
      navigate('/erp/purchasing?tab=receipts');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const total = form.items
    .filter(i => !i.is_return)
    .reduce((sum, i) => sum + (parseFloat(i.received_qty) || 0) * (parseFloat(i.unit_price) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!receipt) return null;

  return (
    <div className="p-6 space-y-6" data-testid="purchase-receipt-edit">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">Mal Kabul Tamamla</h1>
          <p className="text-sm text-[#8a7e70]">
            Sipariş No: {receipt.order_no || '-'} | Tedarikçi: {receipt.supplier_name || '-'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receipt Info */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Mal Kabul Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Depo *</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm(p => ({ ...p, warehouse_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="receipt-warehouse">
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Fatura Numarası</Label>
              <Input
                value={form.invoice_number}
                onChange={(e) => setForm(p => ({ ...p, invoice_number: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                placeholder="Fatura no"
                data-testid="receipt-invoice-no"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Fatura Tarihi</Label>
              <Input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm(p => ({ ...p, invoice_date: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="receipt-invoice-date"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Departman</Label>
              <Select value={form.department_id} onValueChange={(v) => setForm(p => ({ ...p, department_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="receipt-department">
                  <SelectValue placeholder="Departman seçin" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Mal Kabul Yapan Personel</Label>
              <Select value={form.receiver_employee_id} onValueChange={(v) => setForm(p => ({ ...p, receiver_employee_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="receipt-employee">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Notlar</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-10"
                placeholder="Mal kabul notları..."
                data-testid="receipt-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
              <Package size={18} /> Sipariş Kalemleri
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold">Ürün</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Sipariş</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Önceki Teslim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Kalan</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right w-28">Bu Teslimat</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right w-28">Birim Fiyat</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-center w-16">İade</TableHead>
                  <TableHead className="text-[#1a1916] font-bold w-40">İade Açıklama</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.map((item, i) => {
                  const lineTotal = item.is_return ? 0 : (parseFloat(item.received_qty) || 0) * (parseFloat(item.unit_price) || 0);
                  const kalan = parseFloat(item.kalan ?? item.ordered_qty ?? 0);
                  const buTeslimat = parseFloat(item.received_qty || 0);
                  // Kalandan AZ girildiyse satir sari: kalan acik kalacak
                  const isPartial = !item.is_return && buTeslimat > 0 && buTeslimat < kalan;
                  const fazla = !item.is_return && buTeslimat > kalan + 1e-6;

                  return (
                    <TableRow key={i} className={`border-[#d0c4b0] ${item.is_return ? 'bg-red-50' : isPartial ? 'bg-yellow-50' : ''}`}>
                      <TableCell>
                        <div className="font-medium">{item.product_name}</div>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-[#8a7e70]">
                        {item.ordered_qty || 0}
                      </TableCell>
                      <TableCell className="text-right text-[#8a7e70]">
                        {item.onceki_toplam ? item.onceki_toplam : '—'}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-[#3F3E39]">
                        {kalan}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.received_qty}
                          onChange={(e) => updateItem(i, 'received_qty', e.target.value)}
                          className="bg-white border-[#d0c4b0]"
                          disabled={item.is_return}
                          data-testid={`receipt-item-qty-${i}`}
                        />
                        {fazla && (
                          <div className="mt-0.5 text-[11px] text-[#7a6a2e]">
                            kalandan fazla
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[#8a7e70]">{item.unit}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                          className="bg-white border-[#d0c4b0]"
                          disabled={item.is_return}
                          data-testid={`receipt-item-price-${i}`}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {lineTotal.toFixed(2)} TRY
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={item.is_return}
                          onCheckedChange={(checked) => updateItem(i, 'is_return', checked)}
                          data-testid={`receipt-item-return-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        {item.is_return && (
                          <Input
                            value={item.return_reason}
                            onChange={(e) => updateItem(i, 'return_reason', e.target.value)}
                            className="bg-white border-[#d0c4b0]"
                            placeholder="İade sebebi"
                            data-testid={`receipt-item-return-reason-${i}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex items-center justify-between bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
          <div>
            <p className="text-sm text-[#8a7e70]">Toplam Tutar</p>
            <p className="text-2xl font-bold text-[#1a1916]">{total.toFixed(2)} TRY</p>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
              data-testid="complete-receipt-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Mal Kabul Tamamla
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
